/**
 * Dealer detail full-page dashboard.
 *
 * Tabs: Overview . Invoices . Payments . Orders . Offers . Visits . Points.
 *
 * Features:
 *   - Rich overview with circular progress bars, payment health,
 *     credit utilization, activity timeline, and KPI cards.
 *   - Full invoice breakdown with status distribution and progress bars.
 *   - Dedicated payment history tab with DataTable and monthly summary.
 *   - Ledger-based dealer statement with export and print.
 *   - Offers management with inline add/edit.
 *
 * Mock-aware. All data derived from persistent mock stores.
 */

import { useMemo, useRef, useState } from "react";
import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowUpRight,
  Banknote,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Clock,
  CreditCard,
  FileText,
  Landmark,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Receipt,
  ShoppingCart,
  Smartphone,
  TrendingDown,
  TrendingUp,
  User,
  Wallet,
} from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate, formatRelative } from "@bizop/shared/dates";
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Spinner,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { SalesOrderStatusBadge } from "../../../components/sales/SalesOrderStatusBadge";
import {
  DealerForm,
  type DealerFormValues,
} from "../../../components/forms/DealerForm";
import {
  DealerOfferForm,
  type DealerOfferFormValues,
} from "../../../components/forms/DealerOfferForm";
import { useMockData } from "../../../lib/mockMode";
import {
  findDealerTier,
  mockDealerTiers,
  mockDealers,
  mockDealerOffers,
  mockSalesOrders,
  type MockDealer,
  type MockDealerOffer,
  type MockDealerTier,
  type MockSalesOrder,
} from "../../../lib/salesMock";
import { mockProducts, type MockProduct } from "../../../lib/catalogMock";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  dealerOutstandingBaseCents,
  mockInvoices,
  mockPayments,
  mockCreditNotes,
  type MockInvoice,
  type MockPayment,
  type MockCreditNote,
  type PaymentMethod,
} from "../../../lib/invoicingMock";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  printElement,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  PeriodSelector,
  QuickRangeBar,
  defaultPeriodWindow,
  type PeriodWindow,
  type QuickRange,
} from "../../../components/gl/PeriodSelector";

import { mockBranches } from "../../../lib/inventoryMock";

export const Route = createFileRoute("/_app/sales/dealers/$dealerId")({
  component: DealerDetailPage,
});

const TAB_VALUES = [
  "overview",
  "invoices",
  "payments",
  "orders",
  "offers",
  "visits",
  "points",
] as const;
type TabValue = (typeof TAB_VALUES)[number];

const TIER_BADGE_VARIANT_BY_CODE: Record<
  string,
  "neutral" | "info" | "warning" | "success" | "danger"
> = {
  bronze: "neutral",
  silver: "info",
  gold: "warning",
  platinum: "success",
};

function formatDateShort(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function formatMonthYear(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
  });
}

/* -----------------------------------------------------------------
 * Circular Progress Bar -- SVG-based, CSS-only animation
 * ----------------------------------------------------------------- */

function CircularProgress({
  value,
  size = 80,
  strokeWidth = 6,
  label,
  sublabel,
  colorOverride,
}: {
  value: number;
  size?: number;
  strokeWidth?: number;
  label?: string;
  sublabel?: string;
  colorOverride?: string;
}) {
  const clamped = Math.max(0, Math.min(100, value));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;
  const color =
    colorOverride ??
    (clamped >= 80
      ? "var(--success)"
      : clamped >= 40
        ? "var(--warning)"
        : "var(--danger)");
  return (
    <div className="flex flex-col items-center gap-1.5">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--border-subtle)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          className="transform rotate-90 origin-center fill-current text-(--text-primary) font-semibold"
          style={{ fontSize: size >= 80 ? "16px" : size >= 56 ? "13px" : "11px" }}
        >
          {Math.round(clamped)}%
        </text>
      </svg>
      {label ? (
        <span className="text-[10px] uppercase tracking-wide text-(--text-tertiary) font-medium">
          {label}
        </span>
      ) : null}
      {sublabel ? (
        <span className="text-[10px] text-(--text-tertiary)">
          {sublabel}
        </span>
      ) : null}
    </div>
  );
}

/** Mini variant for inline use in invoice cards. */
function CircularProgressMini({
  value,
}: {
  value: number;
}) {
  return <CircularProgress value={value} size={36} strokeWidth={3} />;
}

/* -----------------------------------------------------------------
 * Payment method badge helper
 * ----------------------------------------------------------------- */

const PAYMENT_METHOD_CONFIG: Record<
  PaymentMethod,
  { label: string; icon: typeof Banknote; variant: "neutral" | "info" | "success" | "warning" }
> = {
  cash: { label: "Cash", icon: Banknote, variant: "success" },
  bank_transfer: { label: "Bank", icon: Landmark, variant: "info" },
  cheque: { label: "Cheque", icon: FileText, variant: "neutral" },
  mobile_banking: { label: "Mobile", icon: Smartphone, variant: "info" },
  card: { label: "Card", icon: CreditCard, variant: "info" },
  points: { label: "Points", icon: CircleDollarSign, variant: "warning" },
  credit_note: { label: "Credit Note", icon: FileText, variant: "warning" },
  other: { label: "Other", icon: Wallet, variant: "neutral" },
};

function PaymentMethodBadge({ method }: { method: PaymentMethod }) {
  const config = PAYMENT_METHOD_CONFIG[method] ?? PAYMENT_METHOD_CONFIG.other;
  const Icon = config.icon;
  return (
    <Badge variant={config.variant} size="sm">
      <Icon className="h-3 w-3 mr-0.5" />
      {config.label}
    </Badge>
  );
}

/* -----------------------------------------------------------------
 * Invoice computation helpers
 * ----------------------------------------------------------------- */

type DealerInvoiceStats = {
  totalInvoicedCents: bigint;
  totalPaidCents: bigint;
  totalOutstandingCents: bigint;
  paidPercentage: number;
  invoiceCount: number;
  paidInvoiceCount: number;
  overdueInvoiceCount: number;
  unpaidInvoices: MockInvoice[];
  avgDaysToPay: number | null;
  onTimePaymentRate: number | null;
  lastPayment: { date: number; amountCents: bigint; method: PaymentMethod } | null;
  recentPayments: Array<{
    _id: string;
    date: number;
    amountCents: bigint;
    invoiceRef: string;
    method: PaymentMethod;
    recordedBy: string;
  }>;
  /** All payments for this dealer (not just recent 10), sorted newest first. */
  allPayments: Array<{
    _id: string;
    date: number;
    amountCents: bigint;
    invoiceRef: string;
    invoiceNumber: string;
    method: PaymentMethod;
    externalReference: string | null;
    recordedBy: string;
    paymentNumber: string;
  }>;
  /** Invoice status distribution for visual chart. */
  statusDistribution: Array<{
    status: string;
    label: string;
    count: number;
    totalCents: bigint;
    color: string;
  }>;
  /** Monthly payment summary for the last 6 months. */
  monthlySummary: Array<{
    monthMs: number;
    label: string;
    totalCents: bigint;
    count: number;
  }>;
};

function computeDealerInvoiceStats(
  dealerId: string,
  invoices: readonly MockInvoice[],
  payments: readonly MockPayment[],
): DealerInvoiceStats {
  const now = Date.now();
  const dealerInvoices = invoices.filter(
    (inv) =>
      inv.dealerId === dealerId &&
      inv.status !== "void" &&
      inv.status !== "draft",
  );

  const invoiceById = new Map(dealerInvoices.map((inv) => [inv._id, inv]));
  const dealerInvoiceIds = new Set(dealerInvoices.map((inv) => inv._id));

  const dealerPayments = payments.filter(
    (p) =>
      (dealerInvoiceIds.has(p.invoiceId ?? "") || p.dealerId === dealerId) &&
      p.voidedAt === null,
  );

  let totalInvoicedCents = 0n;
  let totalPaidCents = 0n;
  let paidInvoiceCount = 0;
  let overdueInvoiceCount = 0;
  const unpaidInvoices: MockInvoice[] = [];

  // Status distribution counters
  const statusCounts = new Map<string, { count: number; totalCents: bigint }>();
  for (const inv of dealerInvoices) {
    totalInvoicedCents += inv.totalAmountInBaseCents;
    totalPaidCents += inv.paidAmountInBaseCents;

    // Determine effective status for display
    const isOverdue = inv.dueDate < now && inv.status !== "paid";
    const effectiveStatus = isOverdue ? "overdue" : inv.status;
    if (isOverdue) overdueInvoiceCount++;

    if (inv.status === "paid") {
      paidInvoiceCount++;
    } else {
      unpaidInvoices.push(inv);
    }

    const existing = statusCounts.get(effectiveStatus) ?? { count: 0, totalCents: 0n };
    statusCounts.set(effectiveStatus, {
      count: existing.count + 1,
      totalCents: existing.totalCents + inv.totalAmountInBaseCents,
    });
  }

  const totalOutstandingCents = totalInvoicedCents > totalPaidCents
    ? totalInvoicedCents - totalPaidCents
    : 0n;
  const paidPercentage =
    totalInvoicedCents > 0n
      ? Number((totalPaidCents * 10000n) / totalInvoicedCents) / 100
      : 0;

  // Average days to pay and on-time rate: for paid invoices, compute timing.
  const paidInvoiceDays: number[] = [];
  let onTimeCount = 0;
  for (const inv of dealerInvoices) {
    if (inv.status === "paid") {
      const invPayments = dealerPayments
        .filter((p) => p.invoiceId === inv._id)
        .sort((a, b) => a.receivedAt - b.receivedAt);
      if (invPayments.length > 0) {
        const firstPayDate = invPayments[0]!.receivedAt;
        const days = Math.max(
          0,
          Math.round((firstPayDate - inv.issueDate) / (24 * 60 * 60 * 1000)),
        );
        paidInvoiceDays.push(days);
        // On-time if the last payment was before or on the due date
        const lastPayDate = invPayments[invPayments.length - 1]!.receivedAt;
        if (lastPayDate <= inv.dueDate) {
          onTimeCount++;
        }
      }
    }
  }
  const avgDaysToPay =
    paidInvoiceDays.length > 0
      ? Math.round(
          paidInvoiceDays.reduce((s, d) => s + d, 0) / paidInvoiceDays.length,
        )
      : null;

  const onTimePaymentRate =
    paidInvoiceCount > 0
      ? Math.round((onTimeCount / paidInvoiceCount) * 100)
      : null;

  // Last payment
  const sortedPayments = [...dealerPayments].sort(
    (a, b) => b.receivedAt - a.receivedAt,
  );
  const lastPayment =
    sortedPayments.length > 0
      ? {
          date: sortedPayments[0]!.receivedAt,
          amountCents: sortedPayments[0]!.amountInBaseCents,
          method: sortedPayments[0]!.paymentMethod,
        }
      : null;

  // Recent payments for timeline (last 10)
  const recentPayments = sortedPayments.slice(0, 10).map((p) => {
    const inv = p.invoiceId ? invoiceById.get(p.invoiceId) : null;
    return {
      _id: p._id,
      date: p.receivedAt,
      amountCents: p.amountInBaseCents,
      invoiceRef: inv?.invoiceNumber ?? p.invoiceId ?? "On account",
      method: p.paymentMethod,
      recordedBy: p.bankAccountName ?? "System",
    };
  });

  // All payments for the dedicated Payment History tab
  const allPayments = sortedPayments.map((p) => {
    const inv = p.invoiceId ? invoiceById.get(p.invoiceId) : null;
    return {
      _id: p._id,
      date: p.receivedAt,
      amountCents: p.amountInBaseCents,
      invoiceRef: inv?.invoiceNumber ?? p.invoiceId ?? "On account",
      invoiceNumber: inv?.invoiceNumber ?? "--",
      method: p.paymentMethod,
      externalReference: p.externalReference,
      recordedBy: p.bankAccountName ?? "System",
      paymentNumber: p.paymentNumber,
    };
  });

  // Status distribution for donut/bar chart
  const statusColorMap: Record<string, { label: string; color: string }> = {
    issued: { label: "Issued", color: "var(--accent)" },
    partially_paid: { label: "Partial", color: "var(--warning)" },
    paid: { label: "Paid", color: "var(--success)" },
    overdue: { label: "Overdue", color: "var(--danger)" },
  };

  const statusDistribution = Array.from(statusCounts.entries())
    .map(([status, data]) => ({
      status,
      label: statusColorMap[status]?.label ?? status.replace("_", " "),
      count: data.count,
      totalCents: data.totalCents,
      color: statusColorMap[status]?.color ?? "var(--border-default)",
    }))
    .sort((a, b) => {
      const order = ["paid", "partially_paid", "issued", "overdue"];
      return order.indexOf(a.status) - order.indexOf(b.status);
    });

  // Monthly payment summary (last 6 months)
  const monthMap = new Map<string, { monthMs: number; totalCents: bigint; count: number }>();
  for (const p of sortedPayments) {
    const d = new Date(p.receivedAt);
    const monthStart = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const existing = monthMap.get(key);
    if (existing) {
      existing.totalCents += p.amountInBaseCents;
      existing.count++;
    } else {
      monthMap.set(key, { monthMs: monthStart, totalCents: p.amountInBaseCents, count: 1 });
    }
  }
  const monthlySummary = Array.from(monthMap.values())
    .sort((a, b) => b.monthMs - a.monthMs)
    .slice(0, 6)
    .reverse()
    .map((m) => ({
      ...m,
      label: formatMonthYear(m.monthMs),
    }));

  return {
    totalInvoicedCents,
    totalPaidCents,
    totalOutstandingCents,
    paidPercentage,
    invoiceCount: dealerInvoices.length,
    paidInvoiceCount,
    overdueInvoiceCount,
    unpaidInvoices: unpaidInvoices.sort((a, b) => a.dueDate - b.dueDate),
    avgDaysToPay,
    onTimePaymentRate,
    lastPayment,
    recentPayments,
    allPayments,
    statusDistribution,
    monthlySummary,
  };
}

/* -----------------------------------------------------------------
 * Activity timeline event type
 * ----------------------------------------------------------------- */

type TimelineEvent = {
  id: string;
  type: "payment" | "invoice" | "order" | "credit_note";
  date: number;
  title: string;
  description: string;
  amountCents: bigint | null;
  color: string;
  icon: typeof Banknote;
};

function buildActivityTimeline(
  dealerId: string,
  invoices: readonly MockInvoice[],
  payments: readonly MockPayment[],
  creditNotes: readonly MockCreditNote[],
  orders: readonly MockSalesOrder[],
): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  // Payments
  const dealerInvoiceIds = new Set(
    invoices.filter((i) => i.dealerId === dealerId).map((i) => i._id),
  );
  const invoiceById = new Map(invoices.map((i) => [i._id, i]));

  for (const p of payments) {
    if (!p.invoiceId || !dealerInvoiceIds.has(p.invoiceId) || p.voidedAt !== null) continue;
    const inv = invoiceById.get(p.invoiceId);
    events.push({
      id: `pay_${p._id}`,
      type: "payment",
      date: p.receivedAt,
      title: "Payment received",
      description: `${p.paymentNumber} for ${inv?.invoiceNumber ?? "invoice"}`,
      amountCents: p.amountInBaseCents,
      color: "var(--success)",
      icon: Banknote,
    });
  }

  // Invoices
  for (const inv of invoices) {
    if (inv.dealerId !== dealerId || inv.status === "void" || inv.status === "draft") continue;
    events.push({
      id: `inv_${inv._id}`,
      type: "invoice",
      date: inv.issueDate,
      title: "Invoice issued",
      description: inv.invoiceNumber || "(draft)",
      amountCents: inv.totalAmountInBaseCents,
      color: "var(--accent)",
      icon: FileText,
    });
  }

  // Orders
  for (const o of orders) {
    if (o.dealerId !== dealerId) continue;
    events.push({
      id: `ord_${o._id}`,
      type: "order",
      date: o.placedAt,
      title: `Order ${o.status}`,
      description: o.orderNumber,
      amountCents: o.totalInBaseCents,
      color: "var(--accent)",
      icon: ShoppingCart,
    });
  }

  // Credit notes
  for (const cn of creditNotes) {
    if (cn.dealerId !== dealerId || cn.status === "void") continue;
    events.push({
      id: `cn_${cn._id}`,
      type: "credit_note",
      date: cn.issueDate,
      title: "Credit note issued",
      description: cn.creditNoteNumber,
      amountCents: cn.amountInBaseCents,
      color: "var(--danger)",
      icon: Receipt,
    });
  }

  return events.sort((a, b) => b.date - a.date).slice(0, 15);
}

/* -----------------------------------------------------------------
 * Main page component
 * ----------------------------------------------------------------- */

function DealerDetailPage() {
  const { dealerId } = Route.useParams();
  const navigate = useNavigate();
  const isMock = useMockData();

  // All dealers, sales orders, and offers from persistent state so that
  // newly created records are reflected immediately.
  const [dealersAll] = usePersistentMockState<MockDealer[]>(
    "dealers",
    mockDealers,
  );
  const [salesOrdersAll] = usePersistentMockState<MockSalesOrder[]>(
    "salesOrders",
    mockSalesOrders,
  );
  const [dealerOffersAll, setDealerOffersAll] = usePersistentMockState<MockDealerOffer[]>(
    "dealerOffers",
    mockDealerOffers,
  );

  const resolvedDealer = useMemo<MockDealer | null>(
    () => dealersAll.find((d) => d._id === dealerId) ?? null,
    [dealersAll, dealerId],
  );
  // Local override applied when the user edits dealer fields inline.
  // Falls back to the persistent-state resolved dealer so rehydration is
  // picked up automatically.
  const [editOverride, setEditOverride] = useState<MockDealer | null>(null);
  const dealer = editOverride ?? resolvedDealer;

  // Wrapper that matches the previous setDealer call-sites.
  const setDealer = (value: MockDealer | null) => setEditOverride(value);
  const [tab, setTab] = useState<TabValue>("overview");
  const [editing, setEditing] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState<MockDealerOffer | null>(null);

  // Dual-backed lookups (used in detail panels, forms, and ledger).
  const [dealerTiersState] = usePersistentMockState<MockDealerTier[]>(
    "dealerTiers",
    mockDealerTiers,
  );
  const [productsState] = usePersistentMockState<MockProduct[]>(
    "products",
    mockProducts,
  );
  const [branchesState] = usePersistentMockState<typeof mockBranches>(
    "branches",
    mockBranches,
  );
  const [paymentsState] = usePersistentMockState<MockPayment[]>(
    "payments",
    mockPayments,
  );
  const [creditNotesState] = usePersistentMockState<MockCreditNote[]>(
    "creditNotes",
    mockCreditNotes,
  );

  // When persistent state hasn't hydrated yet the array is empty; show a
  // loading spinner instead of the "not found" screen so the page doesn't
  // flash an error during initial navigation.
  if (!dealer && dealersAll.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24">
        <Spinner size="lg" />
        <p className="text-[13px] text-(--text-tertiary)">Loading dealer...</p>
      </div>
    );
  }

  if (!dealer) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Dealer not found"
          actions={
            <Button
              variant="outline"
              onClick={() => navigate({ to: "/sales/dealers" })}
            >
              <ArrowLeft className="h-4 w-4" /> Back to dealers
            </Button>
          }
        />
        <EmptyState
          title="Unknown dealer"
          description={`No dealer with id "${dealerId}" exists.`}
        />
      </div>
    );
  }

  const tier = useMemo(
    () =>
      dealerTiersState.find((t) => t._id === dealer.tierId) ??
      findDealerTier(dealer.tierId),
    [dealerTiersState, dealer.tierId],
  );
  const branch = useMemo(
    () => branchesState.find((b) => b._id === dealer.branchId) ?? null,
    [branchesState, dealer.branchId],
  );
  const orders = useMemo(
    () =>
      salesOrdersAll
        .filter((o) => o.dealerId === dealer._id)
        .sort((a, b) => b.placedAt - a.placedAt),
    [salesOrdersAll, dealer._id],
  );
  const offers = useMemo(
    () =>
      dealerOffersAll
        .filter((o) => o.dealerId === dealer._id)
        .sort((a, b) => b.validFrom - a.validFrom),
    [dealerOffersAll, dealer._id],
  );
  const lifetimeValue = orders.reduce(
    (s, o) => s + o.totalInBaseCents,
    0n,
  );
  const openOrders = orders.filter(
    (o) =>
      o.status !== "delivered" &&
      o.status !== "rejected" &&
      o.status !== "cancelled",
  ).length;
  const deliveredOrders = orders.filter((o) => o.status === "delivered").length;
  const fulfillmentRate = orders.length > 0
    ? Math.round((deliveredOrders / orders.length) * 100)
    : 0;

  // Average order value
  const avgOrderValue = orders.length > 0
    ? lifetimeValue / BigInt(orders.length)
    : 0n;

  // Last order date
  const lastOrderDate = orders.length > 0 ? orders[0]!.placedAt : null;

  // Live A/R outstanding -- sum of unpaid invoice balances. The previously
  // stored creditUsedCents on the dealer record drifts whenever a payment
  // is recorded but the dealer record isn't refreshed, so we recompute on
  // every render from the current invoices list.
  const [invoicesForCredit] = usePersistentMockState<MockInvoice[]>(
    "invoices",
    mockInvoices,
  );
  const outstandingBaseCents = useMemo(
    () => dealerOutstandingBaseCents(dealer._id, invoicesForCredit),
    [dealer._id, invoicesForCredit],
  );

  // Invoice stats for the enhanced dashboard
  const invoiceStats = useMemo(
    () => computeDealerInvoiceStats(dealer._id, invoicesForCredit, paymentsState),
    [dealer._id, invoicesForCredit, paymentsState],
  );

  // Activity timeline
  const activityTimeline = useMemo(
    () => buildActivityTimeline(dealer._id, invoicesForCredit, paymentsState, creditNotesState, salesOrdersAll),
    [dealer._id, invoicesForCredit, paymentsState, creditNotesState, salesOrdersAll],
  );

  // Form prop options derived from hook state.
  const tierOptions = useMemo(
    () =>
      dealerTiersState.map((t) => ({ id: t._id, displayName: t.displayName })),
    [dealerTiersState],
  );
  const branchOptions = useMemo(
    () => branchesState.map((b) => ({ id: b._id, displayName: b.displayName })),
    [branchesState],
  );
  const dealerOptions = useMemo(
    () => dealersAll.map((d) => ({ id: d._id, displayName: d.displayName })),
    [dealersAll],
  );
  const productOptions = useMemo(
    () =>
      productsState.map((p) => ({
        id: p._id,
        displayName: p.displayName,
        defaultPriceCents: BigInt(
          Math.round(Number(p.standardCostCents) * 1.6),
        ),
      })),
    [productsState],
  );
  const utilizationPct =
    dealer.creditLimitCents > 0n
      ? Number((outstandingBaseCents * 1000n) / dealer.creditLimitCents) / 10
      : 0;
  const availableBaseCents =
    outstandingBaseCents > dealer.creditLimitCents
      ? 0n
      : dealer.creditLimitCents - outstandingBaseCents;
  const isOverLimit = outstandingBaseCents > dealer.creditLimitCents;

  function handleEditSubmit(values: DealerFormValues) {
    if (!isMock || !dealer) return;
    setDealer({
      ...dealer,
      legalName: values.legalName || null,
      displayName: values.displayName,
      tierId: values.tierId,
      branchId: values.branchId,
      primaryUserId: values.primaryUserId,
      creditLimitCents: values.creditLimitCents,
      creditLimitCurrency: values.creditLimitCurrency,
      paymentTermsDays: values.paymentTermsDays,
      addressJson: {
        line1: values.address.line1,
        line2: values.address.line2 || null,
        city: values.address.city,
        country: values.address.country,
      },
      contactJson: {
        phone: values.contact.phone,
        email: values.contact.email || null,
        contactPerson: values.contact.contactPerson || null,
      },
      openingBalanceBaseCents: values.openingBalanceBaseCents,
      openingBalanceAsOf: values.openingBalanceAsOf,
      updatedAt: Date.now(),
    });
    setEditing(false);
    toast.success(`Updated ${dealer.code}`);
  }

  function handleCreateOffer(values: DealerOfferFormValues) {
    if (!isMock) return;
    const now = Date.now();
    const newOffer: MockDealerOffer = {
      _id: `off_local_${now}`,
      dealerId: values.dealerId,
      productId: values.productId,
      variantId: values.variantId,
      unitPriceCents: values.unitPriceCents,
      currency: values.currency,
      minQuantity: values.minQuantity,
      validFrom: values.validFrom,
      validUntil: values.validUntil,
      isActive: values.isActive,
    };
    setDealerOffersAll((prev) => [newOffer, ...prev]);
    setOfferOpen(false);
    toast.success("Offer created (mock)");
  }

  function handleEditOffer(values: DealerOfferFormValues) {
    if (!isMock || !editingOffer) return;
    setDealerOffersAll((prev) =>
      prev.map((o) =>
        o._id === editingOffer._id
          ? {
              ...o,
              productId: values.productId,
              variantId: values.variantId,
              unitPriceCents: values.unitPriceCents,
              currency: values.currency,
              minQuantity: values.minQuantity,
              validFrom: values.validFrom,
              validUntil: values.validUntil,
              isActive: values.isActive,
            }
          : o,
      ),
    );
    setEditingOffer(null);
    toast.success("Offer updated (mock)");
  }

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs displayName={dealer.displayName} />

      {/* -- Hero Section -- */}
      <div className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          {/* Left: Identity */}
          <div className="flex items-start gap-4 min-w-0">
            {/* Avatar / initials */}
            <div className="flex items-center justify-center h-14 w-14 rounded-full bg-(--bg-canvas) border border-(--border-subtle) shrink-0">
              <span className="text-lg font-bold text-(--text-primary)">
                {dealer.displayName.slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="flex flex-col gap-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-semibold text-(--text-primary) truncate">
                  {dealer.displayName}
                </h1>
                {tier ? (
                  <Badge
                    variant={TIER_BADGE_VARIANT_BY_CODE[tier.code] ?? "neutral"}
                  >
                    {tier.displayName}
                  </Badge>
                ) : null}
                {dealer.isActive ? (
                  <StatusPill variant="success" size="sm">Active</StatusPill>
                ) : (
                  <StatusPill variant="neutral" size="sm">Archived</StatusPill>
                )}
              </div>
              <div className="flex items-center gap-3 text-[12px] text-(--text-tertiary) flex-wrap">
                <span className="font-mono">{dealer.code}</span>
                {dealer.legalName ? (
                  <>
                    <span className="text-(--border-default)">|</span>
                    <span>{dealer.legalName}</span>
                  </>
                ) : null}
                {branch ? (
                  <>
                    <span className="text-(--border-default)">|</span>
                    <span>{branch.displayName}</span>
                  </>
                ) : null}
              </div>
              {/* Contact chips */}
              <div className="flex items-center gap-3 text-[12px] text-(--text-secondary) mt-1 flex-wrap">
                {dealer.contactJson.phone ? (
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3 text-(--text-tertiary)" />
                    {dealer.contactJson.phone}
                  </span>
                ) : null}
                {dealer.contactJson.email ? (
                  <span className="flex items-center gap-1">
                    <Mail className="h-3 w-3 text-(--text-tertiary)" />
                    {dealer.contactJson.email}
                  </span>
                ) : null}
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3 text-(--text-tertiary)" />
                  {dealer.addressJson.city}, {dealer.addressJson.country}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3 text-(--text-tertiary)" />
                  Since {formatDateShort(dealer.createdAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setTab("invoices")}>
              <FileText className="h-3.5 w-3.5" /> Statement
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                void navigate({
                  to: "/reports/dealer-statement",
                  search: { dealerId: dealer._id },
                })
              }
            >
              <Receipt className="h-3.5 w-3.5" /> View Statement
            </Button>
            <Button variant="outline" size="sm" onClick={() => setTab("orders")}>
              <ShoppingCart className="h-3.5 w-3.5" /> Orders
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setEditing(true)} aria-label="Edit">
              <Pencil className="size-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* -- KPI strip -- */}
      <section
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
        aria-label="Dealer KPIs"
      >
        <KpiCard
          caption="Outstanding"
          value={formatMoneySmart(invoiceStats.totalOutstandingCents, "BDT", { locale: "en-BD" })}
          hint={
            dealer.creditLimitCents > 0n && isOverLimit
              ? "Over credit limit"
              : `Limit: ${formatMoneySmart(dealer.creditLimitCents, "BDT", { locale: "en-BD" })}`
          }
        />
        <KpiCard
          caption="Payment rate"
          value={`${invoiceStats.paidPercentage.toFixed(1)}%`}
          hint={`${invoiceStats.paidInvoiceCount}/${invoiceStats.invoiceCount} paid`}
        />
        <KpiCard
          caption="Avg days to pay"
          value={invoiceStats.avgDaysToPay !== null ? `${invoiceStats.avgDaysToPay}d` : "--"}
          hint={invoiceStats.avgDaysToPay !== null ? `Terms: ${dealer.paymentTermsDays}d` : "No completed payments"}
        />
        <KpiCard
          caption="Lifetime orders"
          value={String(orders.length)}
          hint={`${openOrders} open`}
        />
        <KpiCard
          caption="Lifetime revenue"
          value={formatMoneySmart(lifetimeValue, "BDT", { locale: "en-BD" })}
          hint={`Avg: ${formatMoneySmart(avgOrderValue, "BDT", { locale: "en-BD" })}`}
        />
        <KpiCard
          caption="Last payment"
          value={
            invoiceStats.lastPayment
              ? formatMoneySmart(invoiceStats.lastPayment.amountCents, "BDT", { locale: "en-BD" })
              : "--"
          }
          hint={
            invoiceStats.lastPayment
              ? formatDateShort(invoiceStats.lastPayment.date)
              : "No payments"
          }
        />
      </section>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={(v: string) => setTab(v as TabValue)}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="invoices">Invoices &amp; Statement</TabsTrigger>
          <TabsTrigger value="payments">
            Payments
            {invoiceStats.allPayments.length > 0 ? (
              <Badge variant="neutral" size="sm" className="ml-1.5">
                {invoiceStats.allPayments.length}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="orders">
            Orders
            {orders.length > 0 ? (
              <Badge variant="neutral" size="sm" className="ml-1.5">
                {orders.length}
              </Badge>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="offers">Offers</TabsTrigger>
          <TabsTrigger value="visits">Visits</TabsTrigger>
          <TabsTrigger value="points">Points</TabsTrigger>
        </TabsList>

        {/* -- Overview -- */}
        <TabsContent value="overview" className="mt-5">
          <div className="flex flex-col gap-5">
            {/* Row 1: Payment Health + Credit Utilization */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Payment Health -- large card spanning 2 columns */}
              <section className="lg:col-span-2 rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-5 flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-(--text-tertiary)" />
                  <h2 className="text-[12px] font-medium uppercase tracking-wide text-(--text-tertiary)">
                    Payment health
                  </h2>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                  {/* Main circular progress */}
                  <CircularProgress
                    value={invoiceStats.paidPercentage}
                    size={110}
                    strokeWidth={8}
                    label="Paid"
                  />

                  {/* Stats grid */}
                  <div className="flex-1 grid grid-cols-2 gap-x-6 gap-y-3 min-w-0">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                        Total invoiced
                      </span>
                      <span className="font-mono tabular-nums text-[15px] text-(--text-primary) font-semibold">
                        {formatMoney(invoiceStats.totalInvoicedCents, "BDT", { locale: "en-BD" })}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                        Total paid
                      </span>
                      <span className="font-mono tabular-nums text-[15px] text-[var(--success)] font-semibold">
                        {formatMoney(invoiceStats.totalPaidCents, "BDT", { locale: "en-BD" })}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                        Outstanding
                      </span>
                      <span className={
                        invoiceStats.totalOutstandingCents > 0n
                          ? "font-mono tabular-nums text-[15px] text-[var(--danger)] font-semibold"
                          : "font-mono tabular-nums text-[15px] text-(--text-secondary)"
                      }>
                        {formatMoney(invoiceStats.totalOutstandingCents, "BDT", { locale: "en-BD" })}
                      </span>
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
                        On-time rate
                      </span>
                      <span className="font-mono tabular-nums text-[15px] text-(--text-primary) font-semibold">
                        {invoiceStats.onTimePaymentRate !== null
                          ? `${invoiceStats.onTimePaymentRate}%`
                          : "--"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Visual breakdown bar */}
                {invoiceStats.totalInvoicedCents > 0n ? (
                  <div className="flex flex-col gap-2 mt-1">
                    <div className="h-2.5 w-full rounded-full bg-(--bg-canvas) overflow-hidden flex">
                      <div
                        className="h-full bg-[var(--success)] transition-[width] duration-700 ease-out rounded-l-full"
                        style={{
                          width: `${Math.max(invoiceStats.paidPercentage, invoiceStats.paidPercentage > 0 ? 2 : 0)}%`,
                        }}
                      />
                      <div
                        className="h-full bg-[var(--danger)] transition-[width] duration-700 ease-out rounded-r-full"
                        style={{
                          width: `${Math.max(100 - invoiceStats.paidPercentage, (100 - invoiceStats.paidPercentage) > 0 ? 2 : 0)}%`,
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block h-2 w-2 rounded-full bg-[var(--success)]" />
                        <span className="text-(--text-tertiary)">
                          Paid {invoiceStats.paidPercentage.toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block h-2 w-2 rounded-full bg-[var(--danger)]" />
                        <span className="text-(--text-tertiary)">
                          Outstanding {(100 - invoiceStats.paidPercentage).toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </section>

              {/* Credit Utilization + Fulfillment */}
              <section className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-5 flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-(--text-tertiary)" />
                  <h2 className="text-[12px] font-medium uppercase tracking-wide text-(--text-tertiary)">
                    Credit &amp; Fulfillment
                  </h2>
                </div>
                <div className="flex items-center justify-around gap-4">
                  {dealer.creditLimitCents > 0n ? (
                    <CircularProgress
                      value={Math.min(utilizationPct, 100)}
                      size={80}
                      strokeWidth={6}
                      label="Credit used"
                      colorOverride={
                        isOverLimit || utilizationPct >= 90
                          ? "var(--danger)"
                          : utilizationPct >= 60
                            ? "var(--warning)"
                            : "var(--accent)"
                      }
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-[24px] font-bold text-(--text-tertiary)">--</span>
                      <span className="text-[10px] uppercase tracking-wide text-(--text-tertiary) font-medium">
                        No limit
                      </span>
                    </div>
                  )}
                  <CircularProgress
                    value={fulfillmentRate}
                    size={80}
                    strokeWidth={6}
                    label="Fulfilled"
                    colorOverride="var(--accent)"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 text-[13px] mt-auto">
                  <Field
                    label="Credit limit"
                    value={formatMoney(dealer.creditLimitCents, "BDT", { locale: "en-BD" })}
                  />
                  <Field
                    label="Available"
                    value={
                      <span className={isOverLimit ? "text-(--text-tertiary)" : undefined}>
                        {formatMoney(availableBaseCents, "BDT", { locale: "en-BD" })}
                      </span>
                    }
                  />
                  <Field
                    label="Payment terms"
                    value={`${dealer.paymentTermsDays} days`}
                  />
                  <Field
                    label="Overdue invoices"
                    value={
                      invoiceStats.overdueInvoiceCount > 0 ? (
                        <span className="text-[var(--danger)] font-semibold">
                          {invoiceStats.overdueInvoiceCount}
                        </span>
                      ) : (
                        <span className="text-[var(--success)]">0</span>
                      )
                    }
                  />
                </div>
                {isOverLimit ? (
                  <p className="text-[11px] text-[var(--danger)] mt-1">
                    Outstanding A/R is{" "}
                    <span className="font-mono">
                      {formatMoney(
                        outstandingBaseCents - dealer.creditLimitCents,
                        "BDT",
                        { locale: "en-BD" },
                      )}
                    </span>{" "}
                    over the credit limit.
                  </p>
                ) : null}
              </section>
            </div>

            {/* Row 2: Invoice Breakdown + Contact Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Invoice Breakdown */}
              <InvoiceBreakdownCard
                stats={invoiceStats}
                onViewAll={() => setTab("invoices")}
              />

              {/* Contact & Dealer Info */}
              <section className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-5 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-(--text-tertiary)" />
                    <h2 className="text-[12px] font-medium uppercase tracking-wide text-(--text-tertiary)">
                      Dealer details
                    </h2>
                  </div>
                  {tier ? (
                    <Badge variant={TIER_BADGE_VARIANT_BY_CODE[tier.code] ?? "neutral"}>
                      {tier.displayName}
                    </Badge>
                  ) : null}
                </div>
                <dl className="grid grid-cols-2 gap-3 text-[13px]">
                  <Field
                    label="Contact person"
                    value={dealer.contactJson.contactPerson ?? "--"}
                  />
                  <Field label="Phone" value={dealer.contactJson.phone} />
                  <Field
                    label="Email"
                    value={dealer.contactJson.email ?? "--"}
                  />
                  <Field
                    label="Address"
                    value={`${dealer.addressJson.line1}, ${dealer.addressJson.city}`}
                  />
                  <Field
                    label="Country"
                    value={dealer.addressJson.country}
                  />
                  <Field
                    label="Branch"
                    value={branch?.displayName ?? dealer.branchId}
                  />
                  <Field
                    label="Currency"
                    value={dealer.creditLimitCurrency}
                  />
                  <Field
                    label="Onboarded"
                    value={formatDateShort(dealer.createdAt)}
                  />
                </dl>

                {/* Utilization bar */}
                {dealer.creditLimitCents > 0n ? (
                  <div className="flex flex-col gap-1.5 mt-2 pt-3 border-t border-(--border-subtle)">
                    <div className="flex items-baseline justify-between text-[11px]">
                      <span className="uppercase tracking-wide text-(--text-tertiary)">
                        Credit utilization
                      </span>
                      <span
                        className={
                          isOverLimit
                            ? "text-[var(--danger)] font-semibold tabular-nums"
                            : utilizationPct >= 90
                              ? "text-[var(--danger)] tabular-nums"
                              : utilizationPct >= 60
                                ? "text-[var(--warning)] tabular-nums"
                                : "text-(--text-secondary) tabular-nums"
                        }
                      >
                        {utilizationPct.toFixed(1)}%
                        {isOverLimit ? " - over limit" : ""}
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-(--bg-canvas) overflow-hidden">
                      <div
                        className={
                          isOverLimit || utilizationPct >= 90
                            ? "h-full bg-[var(--danger)] transition-[width] duration-[var(--duration-fast)]"
                            : utilizationPct >= 60
                              ? "h-full bg-[var(--warning)] transition-[width] duration-[var(--duration-fast)]"
                              : "h-full bg-[var(--accent)] transition-[width] duration-[var(--duration-fast)]"
                        }
                        style={{
                          width: `${Math.min(100, utilizationPct).toFixed(1)}%`,
                        }}
                      />
                    </div>
                  </div>
                ) : null}
              </section>
            </div>

            {/* Row 3: Activity Timeline + Recent Orders */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Activity Timeline */}
              <ActivityTimeline
                events={activityTimeline}
                onViewPayments={() => setTab("payments")}
              />

              {/* Recent Orders */}
              <section className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-5 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-(--text-tertiary)" />
                  <h2 className="text-[12px] font-medium uppercase tracking-wide text-(--text-tertiary)">
                    Recent orders
                  </h2>
                </div>
                {orders.length === 0 ? (
                  <p className="text-[12px] text-(--text-tertiary) py-4">
                    No orders to display.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {orders.slice(0, 6).map((o) => (
                      <li
                        key={o._id}
                        className="flex items-center justify-between gap-3 text-[13px] border-b border-(--border-subtle) last:border-b-0 pb-2 last:pb-0"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <SalesOrderStatusBadge
                            status={o.status}
                            size="sm"
                          />
                          <span className="font-mono text-[12px]">
                            {o.orderNumber}
                          </span>
                          <span className="text-[11px] text-(--text-tertiary)">
                            {formatRelative(o.placedAt)}
                          </span>
                        </div>
                        <span className="font-mono tabular-nums text-(--text-secondary)">
                          {formatMoney(o.totalInBaseCents, "BDT", {
                            locale: "en-BD",
                          })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {orders.length > 6 ? (
                  <button
                    type="button"
                    className="text-[12px] text-[var(--accent)] hover:underline self-start mt-1"
                    onClick={() => setTab("orders")}
                  >
                    View all {orders.length} orders
                  </button>
                ) : null}
              </section>
            </div>

            {/* Row 4: Lifetime summary strip */}
            <section
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
              aria-label="Lifetime summary"
            >
              <MiniStat
                label="Lifetime orders"
                value={String(orders.length)}
                icon={<ShoppingCart className="h-3.5 w-3.5" />}
              />
              <MiniStat
                label="Lifetime value"
                value={formatMoneySmart(lifetimeValue, "BDT", { locale: "en-BD" })}
                icon={<CircleDollarSign className="h-3.5 w-3.5" />}
              />
              <MiniStat
                label="Avg order"
                value={formatMoneySmart(avgOrderValue, "BDT", { locale: "en-BD" })}
                icon={<TrendingUp className="h-3.5 w-3.5" />}
              />
              <MiniStat
                label="Open orders"
                value={String(openOrders)}
                icon={<Clock className="h-3.5 w-3.5" />}
              />
              <MiniStat
                label="Last order"
                value={lastOrderDate ? formatDateShort(lastOrderDate) : "--"}
                icon={<Calendar className="h-3.5 w-3.5" />}
                muted={!lastOrderDate}
              />
              <MiniStat
                label="Credit available"
                value={
                  dealer.creditLimitCents > 0n
                    ? formatMoneySmart(availableBaseCents, "BDT", { locale: "en-BD" })
                    : "--"
                }
                icon={<Wallet className="h-3.5 w-3.5" />}
                muted={dealer.creditLimitCents === 0n}
              />
            </section>
          </div>
        </TabsContent>

        {/* Invoices */}
        <TabsContent value="invoices" className="mt-5">
          <DealerStatement
            dealerId={dealer._id}
            dealerCode={dealer.code}
            dealerDisplayName={dealer.displayName}
            openingArBalanceBaseCents={dealer.openingBalanceBaseCents ?? 0n}
            invoices={invoicesForCredit}
            payments={paymentsState}
            creditNotes={creditNotesState}
            invoiceStats={invoiceStats}
          />
        </TabsContent>

        {/* Payments */}
        <TabsContent value="payments" className="mt-5">
          <PaymentHistoryTab
            stats={invoiceStats}
            dealerCode={dealer.code}
          />
        </TabsContent>

        {/* Orders */}
        <TabsContent value="orders" className="mt-5">
          <OrdersTable orders={orders} />
        </TabsContent>

        {/* Offers */}
        <TabsContent value="offers" className="mt-5">
          <OffersTab
            offers={offers}
            onAddOffer={() => setOfferOpen(true)}
            onEditOffer={(offer) => setEditingOffer(offer)}
            products={productsState}
          />
        </TabsContent>

        {/* Visits */}
        <TabsContent value="visits" className="mt-5">
          <EmptyState
            title="No visits logged yet"
            description="Sales reps will log dealer visits from the field app -- they'll show up here."
          />
        </TabsContent>

        {/* Points */}
        <TabsContent value="points" className="mt-5">
          <EmptyState
            title="Points ledger coming soon"
            description="Loyalty point earn/redeem activity will appear here once the points module ships."
          />
        </TabsContent>
      </Tabs>

      {/* Edit dealer Sheet */}
      <Sheet open={editing} onOpenChange={setEditing}>
        <SheetContent side="right" size="lg">
          <SheetHeader>
            <SheetTitle>Edit {dealer.code}</SheetTitle>
            <SheetDescription>
              Update tier, credit, and contact details.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <DealerForm
              isEdit
              initialValues={{
                code: dealer.code,
                legalName: dealer.legalName ?? "",
                displayName: dealer.displayName,
                tierId: dealer.tierId,
                branchId: dealer.branchId,
                primaryUserId: dealer.primaryUserId,
                creditLimitCents: dealer.creditLimitCents,
                creditLimitCurrency: dealer.creditLimitCurrency,
                paymentTermsDays: dealer.paymentTermsDays,
                address: {
                  line1: dealer.addressJson.line1,
                  line2: dealer.addressJson.line2 ?? "",
                  city: dealer.addressJson.city,
                  country: dealer.addressJson.country,
                },
                contact: {
                  phone: dealer.contactJson.phone,
                  email: dealer.contactJson.email ?? "",
                  contactPerson: dealer.contactJson.contactPerson ?? "",
                },
                openingBalanceBaseCents:
                  dealer.openingBalanceBaseCents ?? 0n,
                openingBalanceAsOf: dealer.openingBalanceAsOf ?? null,
              }}
              tiers={tierOptions}
              branches={branchOptions}
              onSubmit={handleEditSubmit}
              onCancel={() => setEditing(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Add offer Sheet */}
      <Sheet open={offerOpen} onOpenChange={setOfferOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>New offer for {dealer.displayName}</SheetTitle>
            <SheetDescription>
              Offers override standard pricing for this dealer on the chosen
              product.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <DealerOfferForm
              lockedDealerId={dealer._id}
              dealers={dealerOptions}
              products={productOptions}
              onSubmit={handleCreateOffer}
              onCancel={() => setOfferOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Edit offer Sheet */}
      <Sheet
        open={editingOffer !== null}
        onOpenChange={(open: boolean) => {
          if (!open) setEditingOffer(null);
        }}
      >
        <SheetContent side="right" size="md">
          {editingOffer ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit offer</SheetTitle>
                <SheetDescription>
                  Update price, validity window, or active state.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <DealerOfferForm
                  lockedDealerId={dealer._id}
                  initialValues={{
                    dealerId: editingOffer.dealerId,
                    productId: editingOffer.productId,
                    variantId: editingOffer.variantId,
                    unitPriceCents: editingOffer.unitPriceCents,
                    currency: editingOffer.currency,
                    minQuantity: editingOffer.minQuantity,
                    validFrom: editingOffer.validFrom,
                    validUntil: editingOffer.validUntil,
                    isActive: editingOffer.isActive,
                  }}
                  dealers={dealerOptions}
                  products={productOptions}
                  onSubmit={handleEditOffer}
                  onCancel={() => setEditingOffer(null)}
                />
              </SheetBody>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* -----------------------------------------------------------------
 * Sub-components
 * ----------------------------------------------------------------- */

function Breadcrumbs({ displayName }: { displayName: string }) {
  return (
    <nav className="flex items-center gap-1 text-[12px] text-(--text-tertiary)">
      <Link
        to="/sales/dealers"
        className="hover:text-(--text-primary) transition-colors"
      >
        Dealers
      </Link>
      <ChevronRight className="h-3 w-3" aria-hidden />
      <span className="text-(--text-primary)">{displayName}</span>
    </nav>
  );
}

function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </dt>
      <dd className="text-[13px] text-(--text-primary)">{value}</dd>
    </div>
  );
}

/** Compact stat block for the lifetime summary strip. */
function MiniStat({
  label,
  value,
  icon,
  muted,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <div className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) px-4 py-3 flex items-center gap-3">
      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-(--bg-canvas) text-(--text-tertiary) shrink-0">
        {icon}
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-[10px] uppercase tracking-wide text-(--text-tertiary) font-medium">
          {label}
        </span>
        <span className={
          muted
            ? "font-mono tabular-nums text-[14px] font-semibold text-(--text-tertiary)"
            : "font-mono tabular-nums text-[14px] font-semibold text-(--text-primary)"
        }>
          {value}
        </span>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------
 * Visual Invoice Breakdown card
 * ----------------------------------------------------------------- */

function InvoiceBreakdownCard({
  stats,
  onViewAll,
}: {
  stats: DealerInvoiceStats;
  onViewAll: () => void;
}) {
  return (
    <section className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-(--text-tertiary)" />
          <h2 className="text-[12px] font-medium uppercase tracking-wide text-(--text-tertiary)">
            Invoice breakdown
          </h2>
        </div>
        <span className="font-mono tabular-nums text-[16px] font-semibold text-(--text-primary)">
          {stats.invoiceCount} invoices
        </span>
      </div>

      {/* Status distribution bar */}
      {stats.statusDistribution.length > 0 ? (
        <div className="flex flex-col gap-2">
          {/* Stacked bar */}
          <div className="h-3 w-full rounded-full bg-(--bg-canvas) overflow-hidden flex">
            {stats.statusDistribution.map((seg) => {
              const pct = stats.invoiceCount > 0
                ? (seg.count / stats.invoiceCount) * 100
                : 0;
              return (
                <div
                  key={seg.status}
                  className="h-full transition-[width] duration-700 ease-out first:rounded-l-full last:rounded-r-full"
                  style={{
                    width: `${Math.max(pct, pct > 0 ? 2 : 0)}%`,
                    backgroundColor: seg.color,
                  }}
                />
              );
            })}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 flex-wrap text-[11px]">
            {stats.statusDistribution.map((seg) => (
              <div key={seg.status} className="flex items-center gap-1.5">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: seg.color }}
                />
                <span className="text-(--text-tertiary)">
                  {seg.label} ({seg.count})
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-[12px] text-(--text-tertiary)">No invoices yet.</p>
      )}

      {/* Unpaid invoices list */}
      {stats.unpaidInvoices.length > 0 ? (
        <div className="flex flex-col gap-2 mt-1">
          <h3 className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary)">
            Unpaid invoices ({stats.unpaidInvoices.length})
          </h3>
          <ul className="flex flex-col gap-2 max-h-[220px] overflow-y-auto">
            {stats.unpaidInvoices.map((inv) => {
              const invPaidPct =
                inv.totalAmountInBaseCents > 0n
                  ? Number((inv.paidAmountInBaseCents * 10000n) / inv.totalAmountInBaseCents) / 100
                  : 0;
              const balance = inv.totalAmountInBaseCents - inv.paidAmountInBaseCents;
              const isOverdue = inv.dueDate < Date.now() && inv.status !== "paid";
              return (
                <li
                  key={inv._id}
                  className="rounded-[var(--radius-sm)] border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2.5 flex items-center gap-3"
                >
                  <CircularProgressMini value={invPaidPct} />
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[12px] text-(--text-primary) font-medium">
                        {inv.invoiceNumber || "(draft)"}
                      </span>
                      {isOverdue ? (
                        <Badge variant="danger" size="sm">Overdue</Badge>
                      ) : (
                        <Badge variant="info" size="sm">{inv.status.replace("_", " ")}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-(--text-tertiary)">
                      <span>Due {formatDateShort(inv.dueDate)}</span>
                      <span className="text-(--border-default)">|</span>
                      <span>
                        Balance:{" "}
                        <span className="font-mono tabular-nums text-[var(--danger)] font-medium">
                          {formatMoneySmart(balance, "BDT", { locale: "en-BD" })}
                        </span>
                      </span>
                    </div>
                    {/* Mini progress bar for this invoice */}
                    <div className="h-1 w-full rounded-full bg-(--bg-canvas) overflow-hidden mt-1">
                      <div
                        className="h-full bg-[var(--success)] transition-[width] duration-500 ease-out"
                        style={{ width: `${invPaidPct}%` }}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : stats.invoiceCount > 0 ? (
        <div className="flex items-center gap-2 py-2">
          <CheckCircle2 className="h-4 w-4 text-[var(--success)]" />
          <span className="text-[12px] text-[var(--success)] font-medium">
            All invoices fully paid
          </span>
        </div>
      ) : null}

      {stats.invoiceCount > 0 ? (
        <button
          type="button"
          className="text-[12px] text-[var(--accent)] hover:underline self-start"
          onClick={onViewAll}
        >
          View full statement
        </button>
      ) : null}
    </section>
  );
}

/* -----------------------------------------------------------------
 * Activity Timeline (combined events)
 * ----------------------------------------------------------------- */

function ActivityTimeline({
  events,
  onViewPayments,
}: {
  events: TimelineEvent[];
  onViewPayments: () => void;
}) {
  const typeColors: Record<TimelineEvent["type"], string> = {
    payment: "border-[var(--success)]",
    invoice: "border-[var(--accent)]",
    order: "border-[var(--accent)]",
    credit_note: "border-[var(--danger)]",
  };

  const typeBgColors: Record<TimelineEvent["type"], string> = {
    payment: "bg-[var(--success-soft)]",
    invoice: "bg-(--bg-canvas)",
    order: "bg-(--bg-canvas)",
    credit_note: "bg-[var(--danger-soft)]",
  };

  return (
    <section className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-5 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-(--text-tertiary)" />
        <h2 className="text-[12px] font-medium uppercase tracking-wide text-(--text-tertiary)">
          Recent activity
        </h2>
      </div>

      {events.length === 0 ? (
        <p className="text-[12px] text-(--text-tertiary) py-4">
          No activity recorded yet.
        </p>
      ) : (
        <div className="relative">
          {/* Vertical timeline line */}
          <div className="absolute left-[13px] top-3 bottom-3 w-px bg-(--border-subtle)" />

          <ul className="flex flex-col gap-0 max-h-[400px] overflow-y-auto">
            {events.map((ev, idx) => {
              const Icon = ev.icon;
              return (
                <li key={ev.id} className="relative pl-9 pb-3.5 last:pb-0">
                  {/* Timeline dot */}
                  <div
                    className={`absolute left-[7px] top-1 h-[14px] w-[14px] rounded-full border-2 ${typeColors[ev.type]} bg-(--bg-surface) z-10 flex items-center justify-center`}
                  >
                    <Icon className="h-2 w-2" style={{ color: ev.color }} />
                  </div>

                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] text-(--text-primary) font-medium">
                        {ev.title}
                      </span>
                      <span className="text-[11px] text-(--text-tertiary) tabular-nums shrink-0">
                        {formatRelative(ev.date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[11px] text-(--text-secondary)">
                        {ev.description}
                      </span>
                      {ev.amountCents !== null ? (
                        <span
                          className="font-mono tabular-nums text-[12px] font-semibold"
                          style={{ color: ev.color }}
                        >
                          {ev.type === "payment" ? "+" : ""}
                          {formatMoneySmart(ev.amountCents, "BDT", { locale: "en-BD" })}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {events.length > 0 ? (
        <button
          type="button"
          className="text-[12px] text-[var(--accent)] hover:underline self-start mt-1"
          onClick={onViewPayments}
        >
          View all payments
        </button>
      ) : null}
    </section>
  );
}

/* -----------------------------------------------------------------
 * Payment History Tab (NEW)
 * ----------------------------------------------------------------- */

type PaymentRow = DealerInvoiceStats["allPayments"][number];

function PaymentHistoryTab({
  stats,
  dealerCode,
}: {
  stats: DealerInvoiceStats;
  dealerCode: string;
}) {
  const printRef = useRef<HTMLDivElement>(null);

  const columns = useMemo<ColumnDef<PaymentRow>[]>(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="tabular-nums text-(--text-secondary)">
            {formatDateShort(row.original.date)}
          </span>
        ),
      },
      {
        accessorKey: "paymentNumber",
        header: "Payment #",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.paymentNumber}
          </span>
        ),
        meta: { mono: true },
      },
      {
        accessorKey: "invoiceNumber",
        header: "Invoice #",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="font-mono text-[12px] text-(--text-secondary)">
            {row.original.invoiceNumber}
          </span>
        ),
        meta: { mono: true },
      },
      {
        accessorKey: "amountCents",
        header: "Amount",
        meta: { numeric: true, mono: true },
        cell: ({ row }: { row: { original: any } }) => (
          <span className="font-mono tabular-nums font-semibold text-[var(--success)]">
            {formatMoney(row.original.amountCents, "BDT", { locale: "en-BD" })}
          </span>
        ),
      },
      {
        id: "method",
        header: "Method",
        cell: ({ row }: { row: { original: any } }) => (
          <PaymentMethodBadge method={row.original.method} />
        ),
      },
      {
        accessorKey: "externalReference",
        header: "Reference",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="text-[12px] text-(--text-secondary)">
            {row.original.externalReference ?? "--"}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
    ],
    [],
  );

  // Compute running total
  const runningTotal = stats.allPayments.reduce(
    (sum, p) => sum + p.amountCents,
    0n,
  );

  function handleCsv(ext: "csv" | "xls") {
    type CsvRow = {
      date: string;
      paymentNumber: string;
      invoiceNumber: string;
      amount: bigint;
      method: string;
      reference: string;
    };
    const csvRows: CsvRow[] = stats.allPayments.map((p) => ({
      date: formatDateShort(p.date),
      paymentNumber: p.paymentNumber,
      invoiceNumber: p.invoiceNumber,
      amount: p.amountCents,
      method: p.method,
      reference: p.externalReference ?? "",
    }));
    const cols: ExportColumn<CsvRow>[] = [
      { key: "date", label: "Date" },
      { key: "paymentNumber", label: "Payment #" },
      { key: "invoiceNumber", label: "Invoice #" },
      { key: "amount", label: "Amount (BDT)" },
      { key: "method", label: "Method" },
      { key: "reference", label: "Reference" },
    ];
    downloadCsv(
      exportFilename(`${dealerCode}-payments`, ext),
      toCsv(csvRows, cols),
    );
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: `Payment History - ${dealerCode}`,
    });
  }

  return (
    <div className="flex flex-col gap-5" ref={printRef}>
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-end gap-3" data-print-hide="">
        <ExportButton
          onCsv={() => handleCsv("csv")}
          onExcel={() => handleCsv("xls")}
          onPrint={handlePrint}
        />
      </div>

      {/* KPI strip */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Payment KPIs"
      >
        <KpiCard
          caption="Total payments"
          value={String(stats.allPayments.length)}
          hint="All-time count"
        />
        <KpiCard
          caption="Total collected"
          value={formatMoneySmart(runningTotal, "BDT", { locale: "en-BD" })}
          hint="Cumulative"
        />
        <KpiCard
          caption="Avg days to pay"
          value={stats.avgDaysToPay !== null ? `${stats.avgDaysToPay}d` : "--"}
          hint={stats.avgDaysToPay !== null ? "Across paid invoices" : "No data"}
        />
        <KpiCard
          caption="On-time rate"
          value={stats.onTimePaymentRate !== null ? `${stats.onTimePaymentRate}%` : "--"}
          hint={stats.onTimePaymentRate !== null ? `${stats.paidInvoiceCount} invoices` : "No data"}
        />
      </section>

      {/* Monthly Summary */}
      {stats.monthlySummary.length > 0 ? (
        <section className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-5 flex flex-col gap-3">
          <h2 className="text-[12px] font-medium uppercase tracking-wide text-(--text-tertiary)">
            Monthly collection
          </h2>
          <div className="flex flex-col gap-2">
            {(() => {
              const maxCents = stats.monthlySummary.reduce(
                (max, m) => (m.totalCents > max ? m.totalCents : max),
                0n,
              );
              return stats.monthlySummary.map((m) => {
                const barPct = maxCents > 0n
                  ? Number((m.totalCents * 100n) / maxCents)
                  : 0;
                return (
                  <div key={m.monthMs} className="flex items-center gap-3">
                    <span className="text-[12px] text-(--text-secondary) w-20 shrink-0 tabular-nums">
                      {m.label}
                    </span>
                    <div className="flex-1 h-5 rounded-sm bg-(--bg-canvas) overflow-hidden">
                      <div
                        className="h-full bg-[var(--success)] rounded-sm transition-[width] duration-500 ease-out flex items-center justify-end px-1.5"
                        style={{ width: `${Math.max(barPct, barPct > 0 ? 4 : 0)}%` }}
                      >
                        {barPct > 25 ? (
                          <span className="text-[10px] font-mono tabular-nums text-white font-medium">
                            {formatMoneySmart(m.totalCents, "BDT", { locale: "en-BD" })}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {barPct <= 25 ? (
                      <span className="text-[11px] font-mono tabular-nums text-(--text-secondary) shrink-0">
                        {formatMoneySmart(m.totalCents, "BDT", { locale: "en-BD" })}
                      </span>
                    ) : null}
                    <span className="text-[10px] text-(--text-tertiary) w-14 shrink-0 text-right tabular-nums">
                      {m.count} pmt{m.count !== 1 ? "s" : ""}
                    </span>
                  </div>
                );
              });
            })()}
          </div>
        </section>
      ) : null}

      {/* Payments table */}
      <DataTable
        data={stats.allPayments}
        columns={columns}
        getRowId={(r) => r._id}
        emptyTitle="No payments recorded"
        emptyDescription="Payments will appear here as they are recorded against this dealer's invoices."
        emptyIcon={<Banknote className="h-5 w-5" aria-hidden />}
      />

      {/* Running total footer */}
      {stats.allPayments.length > 0 ? (
        <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) px-4 py-3 flex items-center justify-between text-[13px]">
          <span className="text-(--text-secondary)">Total collected</span>
          <span className="font-mono tabular-nums font-semibold text-[var(--success)]">
            {formatMoney(runningTotal, "BDT", { locale: "en-BD" })}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/* -----------------------------------------------------------------
 * Orders table
 * ----------------------------------------------------------------- */

function OrdersTable({ orders }: { orders: MockSalesOrder[] }) {
  const columns = useMemo<ColumnDef<MockSalesOrder>[]>(
    () => [
      {
        accessorKey: "orderNumber",
        header: "Order #",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="font-mono text-[12px]">
            {row.original.orderNumber}
          </span>
        ),
        meta: { mono: true },
      },
      {
        accessorKey: "placedAt",
        header: "Placed",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatRelative(row.original.placedAt)}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }: { row: { original: any } }) => (
          <SalesOrderStatusBadge status={row.original.status} />
        ),
      },
      {
        id: "total",
        header: "Total",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="font-mono tabular-nums">
            {formatMoney(row.original.totalInBaseCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
        meta: { numeric: true, mono: true },
      },
      {
        accessorKey: "updatedAt",
        header: "Updated",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatRelative(row.original.updatedAt)}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
    ],
    [],
  );
  return (
    <DataTable
      data={orders}
      columns={columns}
      getRowId={(r) => r._id}
      emptyTitle="No orders for this dealer"
      emptyDescription="When this dealer submits orders, they'll appear here."
    />
  );
}

function OffersTab({
  offers,
  onAddOffer,
  onEditOffer,
  products,
}: {
  offers: MockDealerOffer[];
  onAddOffer: () => void;
  onEditOffer: (offer: MockDealerOffer) => void;
  products: MockProduct[];
}) {
  const productsById = useMemo(
    () => new Map(products.map((p) => [p._id, p])),
    [products],
  );
  const columns = useMemo<ColumnDef<MockDealerOffer>[]>(
    () => [
      {
        accessorKey: "productId",
        header: "Product",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="text-(--text-primary)">
            {productsById.get(row.original.productId)?.displayName ??
              row.original.productId}
          </span>
        ),
      },
      {
        id: "price",
        header: "Unit price",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="font-mono tabular-nums">
            {formatMoney(row.original.unitPriceCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
        meta: { numeric: true, mono: true },
      },
      {
        accessorKey: "minQuantity",
        header: "Min qty",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="font-mono tabular-nums">
            {row.original.minQuantity}
          </span>
        ),
        meta: { numeric: true },
      },
      {
        accessorKey: "validFrom",
        header: "Valid from",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatDateShort(row.original.validFrom)}
          </span>
        ),
      },
      {
        id: "validUntil",
        header: "Valid until",
        cell: ({ row }: { row: { original: any } }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {row.original.validUntil
              ? formatDateShort(row.original.validUntil)
              : "--"}
          </span>
        ),
      },
      {
        id: "active",
        header: "Active",
        cell: ({ row }: { row: { original: any } }) =>
          row.original.isActive ? (
            <StatusPill variant="success" size="sm">
              Active
            </StatusPill>
          ) : (
            <StatusPill variant="neutral" size="sm">
              Inactive
            </StatusPill>
          ),
      },
    ],
    [productsById],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Button onClick={onAddOffer}>
          <Plus className="h-3.5 w-3.5" /> Add offer
        </Button>
      </div>
      <DataTable
        data={offers}
        columns={columns}
        getRowId={(o) => o._id}
        onRowClick={(row) => onEditOffer(row.original)}
        emptyTitle="No dealer offers"
        emptyDescription="Add a negotiated price for this dealer to override the standard catalog price."
        emptyAction={
          <Button onClick={onAddOffer}>
            <Plus className="h-3.5 w-3.5" /> Add offer
          </Button>
        }
      />
    </div>
  );
}

/* -----------------------------------------------------------------
 * Dealer Statement (Invoices tab)
 * ----------------------------------------------------------------- */

type LedgerRow = {
  id: string;
  dateMs: number;
  type: "Invoice" | "Payment" | "Credit Note";
  reference: string;
  description: string;
  debitCents: bigint;
  creditCents: bigint;
  runningBalanceCents: bigint;
};

function buildLedger(
  dealerId: string,
  openingBalanceCents: bigint,
  fromMs: number,
  toMs: number,
  invoices: MockInvoice[],
  payments: MockPayment[],
  creditNotes: MockCreditNote[],
): {
  rows: LedgerRow[];
  openingCents: bigint;
  totalDebitCents: bigint;
  totalCreditCents: bigint;
  closingCents: bigint;
} {
  // Collect all events for this dealer within the period.
  // Invoices for this dealer (non-void, non-draft, issued in period).
  const invoicesForDealer = invoices.filter(
    (inv) =>
      inv.dealerId === dealerId &&
      inv.status !== "void" &&
      inv.status !== "draft" &&
      inv.issueDate >= fromMs &&
      inv.issueDate <= toMs,
  );

  // Build a set of this dealer's invoice IDs to filter payments and credit notes.
  const dealerInvoiceIds = new Set(invoices
    .filter((inv) => inv.dealerId === dealerId)
    .map((inv) => inv._id));

  // Payments linked to dealer's invoices, received in period, not voided.
  const paymentsForDealer = payments.filter(
    (pay) =>
      dealerInvoiceIds.has(pay.invoiceId ?? "") &&
      pay.voidedAt === null &&
      pay.receivedAt >= fromMs &&
      pay.receivedAt <= toMs,
  );

  // Credit notes for this dealer issued in period, not void.
  const creditNotesForDealer = creditNotes.filter(
    (cn) =>
      cn.dealerId === dealerId &&
      cn.status !== "void" &&
      cn.issueDate >= fromMs &&
      cn.issueDate <= toMs,
  );

  // Build raw event list and sort chronologically.
  type RawEvent =
    | { kind: "invoice"; dateMs: number; id: string }
    | { kind: "payment"; dateMs: number; id: string }
    | { kind: "credit_note"; dateMs: number; id: string };

  const events: RawEvent[] = [
    ...invoicesForDealer.map((inv) => ({
      kind: "invoice" as const,
      dateMs: inv.issueDate,
      id: inv._id,
    })),
    ...paymentsForDealer.map((pay) => ({
      kind: "payment" as const,
      dateMs: pay.receivedAt,
      id: pay._id,
    })),
    ...creditNotesForDealer.map((cn) => ({
      kind: "credit_note" as const,
      dateMs: cn.issueDate,
      id: cn._id,
    })),
  ];

  events.sort((a, b) => a.dateMs - b.dateMs);

  const invoiceMap = new Map(invoices.map((i) => [i._id, i]));
  const paymentMap = new Map(payments.map((p) => [p._id, p]));
  const creditNoteMap = new Map(creditNotes.map((c) => [c._id, c]));

  let running = openingBalanceCents;
  let totalDebit = 0n;
  let totalCredit = 0n;
  const rows: LedgerRow[] = [];

  for (const event of events) {
    if (event.kind === "invoice") {
      const inv = invoiceMap.get(event.id);
      if (!inv) continue;
      const amount = inv.totalAmountInBaseCents;
      running += amount;
      totalDebit += amount;
      rows.push({
        id: event.id,
        dateMs: event.dateMs,
        type: "Invoice",
        reference: inv.invoiceNumber || "(draft)",
        description: inv.notes ?? `Invoice ${inv.invoiceNumber || ""}`.trim(),
        debitCents: amount,
        creditCents: 0n,
        runningBalanceCents: running,
      });
    } else if (event.kind === "payment") {
      const pay = paymentMap.get(event.id);
      if (!pay) continue;
      const amount = pay.amountInBaseCents;
      running -= amount;
      totalCredit += amount;
      rows.push({
        id: event.id,
        dateMs: event.dateMs,
        type: "Payment",
        reference: pay.paymentNumber,
        description: pay.notes ?? `Payment via ${pay.paymentMethod}`,
        debitCents: 0n,
        creditCents: amount,
        runningBalanceCents: running,
      });
    } else {
      const cn = creditNoteMap.get(event.id);
      if (!cn) continue;
      const amount = cn.amountInBaseCents;
      running -= amount;
      totalCredit += amount;
      rows.push({
        id: event.id,
        dateMs: event.dateMs,
        type: "Credit Note",
        reference: cn.creditNoteNumber,
        description: cn.notes ?? cn.creditReason,
        debitCents: 0n,
        creditCents: amount,
        runningBalanceCents: running,
      });
    }
  }

  return {
    rows,
    openingCents: openingBalanceCents,
    totalDebitCents: totalDebit,
    totalCreditCents: totalCredit,
    closingCents: running,
  };
}

function DealerStatement({
  dealerId,
  dealerCode,
  dealerDisplayName,
  openingArBalanceBaseCents,
  invoices,
  payments,
  creditNotes,
  invoiceStats,
}: {
  dealerId: string;
  dealerCode: string;
  dealerDisplayName: string;
  openingArBalanceBaseCents: bigint;
  invoices: MockInvoice[];
  payments: MockPayment[];
  creditNotes: MockCreditNote[];
  invoiceStats: DealerInvoiceStats;
}) {
  const [period, setPeriod] = useState<PeriodWindow>(defaultPeriodWindow);
  const [quickRange, setQuickRange] = useState<QuickRange>("this_month");
  const printRef = useRef<HTMLDivElement>(null);

  const { rows, openingCents, totalDebitCents, totalCreditCents, closingCents } =
    useMemo(
      () =>
        buildLedger(
          dealerId,
          openingArBalanceBaseCents,
          period.fromDate,
          period.toDate,
          invoices,
          payments,
          creditNotes,
        ),
      [
        dealerId,
        openingArBalanceBaseCents,
        period.fromDate,
        period.toDate,
        invoices,
        payments,
        creditNotes,
      ],
    );

  function handleQuickRange(key: QuickRange, window: PeriodWindow | null) {
    setQuickRange(key);
    if (window) {
      setPeriod(window);
    } else {
      // "Custom" -- switch PeriodSelector into custom mode, keep existing dates
      setPeriod((prev) => ({ ...prev, key: "custom" }));
    }
  }

  const columns = useMemo<ColumnDef<LedgerRow>[]>(
    () => [
      {
        accessorKey: "dateMs",
        header: "Date",
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span className="tabular-nums text-(--text-secondary)">
            {formatDate(row.original.dateMs, { style: "short" })}
          </span>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }: { row: { original: LedgerRow } }) => {
          const t = row.original.type;
          return (
            <Badge
              variant={
                t === "Invoice"
                  ? "info"
                  : t === "Payment"
                    ? "success"
                    : "warning"
              }
              size="sm"
            >
              {t}
            </Badge>
          );
        },
      },
      {
        accessorKey: "reference",
        header: "Reference #",
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.reference}
          </span>
        ),
        meta: { mono: true },
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span className="text-(--text-secondary) text-[12px] truncate max-w-[200px] block">
            {row.original.description}
          </span>
        ),
      },
      {
        accessorKey: "debitCents",
        header: "Debit",
        meta: { numeric: true, mono: true },
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span className="font-mono tabular-nums text-(--text-primary)">
            {row.original.debitCents > 0n
              ? formatMoney(row.original.debitCents, "BDT", { locale: "en-BD" })
              : "--"}
          </span>
        ),
      },
      {
        accessorKey: "creditCents",
        header: "Credit",
        meta: { numeric: true, mono: true },
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {row.original.creditCents > 0n
              ? formatMoney(row.original.creditCents, "BDT", { locale: "en-BD" })
              : "--"}
          </span>
        ),
      },
      {
        accessorKey: "runningBalanceCents",
        header: "Balance",
        meta: { numeric: true, mono: true },
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span
            className={
              row.original.runningBalanceCents < 0n
                ? "font-mono tabular-nums font-semibold text-(--danger)"
                : "font-mono tabular-nums font-semibold text-(--text-primary)"
            }
          >
            {formatMoney(row.original.runningBalanceCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
      },
    ],
    [],
  );

  function statementFilename(ext: "csv" | "xls"): string {
    const fromStr = new Date(period.fromDate).toISOString().slice(0, 10);
    const toStr = new Date(period.toDate).toISOString().slice(0, 10);
    const slug = dealerCode.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    return `bizop-statement-${slug}-${fromStr}-to-${toStr}.${ext}`;
  }

  function handleCsv(ext: "csv" | "xls") {
    type CsvRow = {
      date: string;
      type: string;
      reference: string;
      description: string;
      debit: bigint;
      credit: bigint;
      balance: bigint;
    };
    const csvRows: CsvRow[] = rows.map((r) => ({
      date: formatDate(r.dateMs, { style: "short" }),
      type: r.type,
      reference: r.reference,
      description: r.description,
      debit: r.debitCents,
      credit: r.creditCents,
      balance: r.runningBalanceCents,
    }));
    const cols: ExportColumn<CsvRow>[] = [
      { key: "date", label: "Date" },
      { key: "type", label: "Type" },
      { key: "reference", label: "Reference #" },
      { key: "description", label: "Description" },
      { key: "debit", label: "Debit (BDT)" },
      { key: "credit", label: "Credit (BDT)" },
      { key: "balance", label: "Running Balance (BDT)" },
    ];
    downloadCsv(statementFilename(ext), toCsv(csvRows, cols));
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: `Dealer Statement — ${dealerDisplayName}`,
      subtitle: `${formatDate(period.fromDate, { style: "short" })} → ${formatDate(period.toDate, { style: "short" })}`,
    });
  }

  return (
    <div className="flex flex-col gap-5" ref={printRef}>
      {/* Invoice status visual summary */}
      {invoiceStats.statusDistribution.length > 0 ? (
        <section className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-surface) p-5 flex flex-col gap-3">
          <h2 className="text-[12px] font-medium uppercase tracking-wide text-(--text-tertiary)">
            Invoice status overview
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {invoiceStats.statusDistribution.map((seg) => (
              <div
                key={seg.status}
                className="rounded-[var(--radius-sm)] border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2.5 flex items-center gap-3"
              >
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `color-mix(in srgb, ${seg.color} 15%, transparent)` }}
                >
                  <span
                    className="text-[16px] font-bold tabular-nums"
                    style={{ color: seg.color }}
                  >
                    {seg.count}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-[12px] font-medium text-(--text-primary)">
                    {seg.label}
                  </span>
                  <span className="text-[11px] font-mono tabular-nums text-(--text-tertiary)">
                    {formatMoneySmart(seg.totalCents, "BDT", { locale: "en-BD" })}
                  </span>
                </div>
              </div>
            ))}
          </div>
          {/* Stacked bar */}
          <div className="h-2.5 w-full rounded-full bg-(--bg-canvas) overflow-hidden flex">
            {invoiceStats.statusDistribution.map((seg) => {
              const pct = invoiceStats.invoiceCount > 0
                ? (seg.count / invoiceStats.invoiceCount) * 100
                : 0;
              return (
                <div
                  key={seg.status}
                  className="h-full transition-[width] duration-700 ease-out first:rounded-l-full last:rounded-r-full"
                  style={{
                    width: `${Math.max(pct, pct > 0 ? 2 : 0)}%`,
                    backgroundColor: seg.color,
                  }}
                />
              );
            })}
          </div>
        </section>
      ) : null}

      {/* Account Statement header — visually distinct section divider */}
      <div className="rounded-[var(--radius-md)] border border-(--accent)/20 bg-(--accent)/5 px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-(--accent)/10 flex items-center justify-center shrink-0">
            <FileText className="h-4 w-4 text-(--accent)" />
          </div>
          <div>
            <h2 className="text-[14px] font-semibold text-(--text-primary)">
              Account Statement
            </h2>
            <p className="text-[12px] text-(--text-secondary)">
              {dealerDisplayName} &middot;{" "}
              {formatDate(period.fromDate, { style: "short" })} →{" "}
              {formatDate(period.toDate, { style: "short" })}
            </p>
          </div>
        </div>
        <ExportButton
          onCsv={() => handleCsv("csv")}
          onExcel={() => handleCsv("xls")}
          onPrint={handlePrint}
        />
      </div>

      {/* Controls: Quick range bar + custom date picker */}
      <div className="flex flex-col gap-2" data-print-hide="">
        <QuickRangeBar value={quickRange} onChange={handleQuickRange} />
        {quickRange === "custom" ? (
          <PeriodSelector value={period} onChange={setPeriod} />
        ) : null}
      </div>

      {/* KPI strip */}
      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Statement KPIs"
      >
        <KpiCard
          caption="Opening balance"
          value={formatMoneySmart(openingCents, "BDT", { locale: "en-BD" })}
          hint="Carried forward"
        />
        <KpiCard
          caption="Total invoiced"
          value={formatMoneySmart(totalDebitCents, "BDT", { locale: "en-BD" })}
          hint="Period debits"
        />
        <KpiCard
          caption="Total paid"
          value={formatMoneySmart(totalCreditCents, "BDT", { locale: "en-BD" })}
          hint="Period credits"
        />
        <KpiCard
          caption="Closing balance"
          value={formatMoneySmart(closingCents, "BDT", { locale: "en-BD" })}
          hint="As of period end"
        />
      </section>

      {/* Ledger table */}
      <DataTable
        data={rows}
        columns={columns}
        getRowId={(r) => r.id}
        emptyTitle="No transactions in this period"
        emptyDescription="There are no invoices, payments, or credit notes for this dealer in the selected period."
        emptyIcon={<FileText className="h-5 w-5" aria-hidden />}
      />

      {/* Closing balance footer */}
      <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) px-4 py-3 flex items-center justify-between text-[13px]">
        <span className="text-(--text-secondary)">
          Closing balance:{" "}
          <span className="text-(--text-tertiary) text-[12px]">
            {formatDate(period.toDate, { style: "medium" })}
          </span>
        </span>
        <span
          className={
            closingCents < 0n
              ? "font-mono tabular-nums font-semibold text-(--danger)"
              : "font-mono tabular-nums font-semibold text-(--text-primary)"
          }
        >
          {formatMoney(closingCents, "BDT", { locale: "en-BD" })}
        </span>
      </div>
    </div>
  );
}
